import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { formatDate, getYouTubeVideoId, parseCategories, getImageUrl, convertStarsToHtml } from "../utils/helpers";
import { getApiUrl } from "../config/api";
import { InlineShareButtons } from "../components/ShareButton";
import ImageCarousel from "../components/ImageCarousel";
import Embed from "../components/Embed";
import MetaTags from "../components/MetaTags";

const SingleNewsPage = ({ allArticles, lang, textData, imageVersion }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = allArticles.find((a) => a.slug === slug);
  const images =
    article &&
    article.image &&
    Array.isArray(article.image) &&
    article.image.length > 0
      ? article.image.map((img) => `${img}?v=${imageVersion}`)
      : [getImageUrl(article, "News", imageVersion)];
  const hasMultipleImages =
    article &&
    article.image &&
    Array.isArray(article.image) &&
    images.length > 1;

  const currentCategory = useMemo(() => {
    if (!article || !article.categories) return null;
    const categories = parseCategories(article.categories);
    return categories[0] || null;
  }, [article]);

  const relatedArticles = useMemo(() => {
    if (!currentCategory || !article) return [];
    return allArticles.filter((a) => {
      if (a.id === article.id) return false;
      const aCategories = parseCategories(a.categories);
      return aCategories.includes(currentCategory);
    });
  }, [allArticles, article, currentCategory]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalIdx, setModalIdx] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (slug) {
      fetch(`${getApiUrl("VIEW_NEWS")}/${slug}`, { method: "POST" }).catch(() => {});
    }
  }, [slug]);

  if (!article) {
    return (
      <div className="flex-grow flex justify-center items-center h-screen text-red-500">
        <MetaTags
          title="Article Not Found"
          description="The article you are looking for does not exist or has been moved."
        />
        Article not found.
      </div>
    );
  }

  const metaDescription = article.content
    ? article.content.substring(0, 160) + "..."
    : "Read more at Amdavad Post.";

  return (
    <main className="flex-grow container mx-auto px-4 py-8">
      <MetaTags title={article.title} description={metaDescription} />
      <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 max-w-4xl mx-auto">
        <div className="mb-4">
          <h3
            className="text-2xl md:text-3xl font-bold break-words overflow-hidden"
            style={{ color: "#215077", overflowWrap: "anywhere" }}
          >
            {article.title}
          </h3>
        </div>
        <div className="flex items-center justify-between mb-6 text-sm">
          <div className="flex items-center space-x-4">
            <div className="flex flex-wrap gap-2">
              {(
                parseCategories(article.categories) || [currentCategory || "News"]
              ).map((c, idx) => (
                <span
                  key={idx}
                  className="font-semibold text-red-600 bg-red-100 px-2 py-1 rounded-full self-start inline-block capitalize"
                >
                  {c}
                </span>
              ))}
            </div>
            <p className="text-gray-500">{formatDate(article.createdAt)}</p>
          </div>
          <InlineShareButtons
            url={`${window.location.origin}/news/${article.slug}`}
            title={article.title}
          />
        </div>
        {hasMultipleImages ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-8">
            {images.map((img, idx) => (
              <button
                key={idx}
                className="focus:outline-none"
                onClick={() => {
                  setModalOpen(true);
                  setModalIdx(idx);
                }}
              >
                <img
                  src={img}
                  alt={`Gallery ${idx + 1}`}
                  className="w-full h-40 object-cover rounded-lg border border-gray-200 hover:shadow-lg transition"
                />
              </button>
            ))}
          </div>
        ) : (
          <img
            src={images[0]}
            alt={article.title}
            className="w-full h-auto max-h-[500px] object-cover rounded-lg mb-8"
          />
        )}
        {modalOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
            onClick={() => setModalOpen(false)}
          >
            <button
              className="absolute top-6 right-8 text-white text-4xl font-bold bg-black/60 rounded-full px-3 py-1 z-50 hover:bg-black/80 focus:outline-none"
              style={{ marginTop: "0" }}
              onClick={() => setModalOpen(false)}
            >
              &times;
            </button>
            <div
              className="relative w-full max-w-2xl p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={images[modalIdx]}
                alt={`Gallery ${modalIdx + 1}`}
                className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
              />
            </div>
          </div>
        )}

        <div className="text-gray-800 text-xl leading-relaxed mb-8 break-words overflow-hidden" style={{ overflowWrap: "anywhere" }}>
          <div
            dangerouslySetInnerHTML={{
              __html: (function () {
                const raw = article.content || "";
                if (/<[^>]+>/g.test(raw)) return raw;
                const escaped = String(raw)
                  .replace(/&/g, "&amp;")
                  .replace(/</g, "&lt;")
                  .replace(/>/g, "&gt;")
                  .replace(/"/g, "&quot;")
                  .replace(/'/g, "&#039;");
                const converted = escaped.replace(
                  /\*(.+?)\*/g,
                  (m, p1) => `<strong>${p1}</strong>`
                );
                return converted.replace(/\r?\n/g, "<br />");
              })(),
            }}
          />
        </div>

        {article.reference_url && (
          <div className="mt-6 mb-8 text-sm text-gray-600">
            <span className="font-bold text-gray-800">Reference: </span>
            <a
              href={article.reference_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 hover:underline break-all"
            >
              {article.reference_url}
            </a>
          </div>
        )}
        {article.embedUrl && (
          <div className="mt-6 mb-8 text-sm text-gray-600">
            <span className="font-bold text-gray-800">
              {
                article.embedUrl.toLowerCase().includes("x.com") ||
                article.embedUrl.toLowerCase().includes("twitter.com")
                  ? "X POST : "
                  : article.embedUrl.toLowerCase().includes("instagram.com")
                  ? "INSTAGRAM POST : "
                  : article.embedUrl.toLowerCase().includes("facebook.com")
                  ? "FACEBOOK POST : "
                  : "Embed URL: "
              }{" "}
            </span>
            <a
              href={article.embedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 hover:underline break-all"
            >
              {article.embedUrl}
            </a>
          </div>
        )}
       {article && (article.embedUrl || article.embedURL || article.embed_url) && (
          <div className="mt-6">
            <Embed
              url={article.embedUrl || article.embedURL || article.embed_url}
              className="w-full"
            />
          </div>
        )}

        {article.youtubeUrl && (
          <div className="my-8 aspect-video">
            <iframe
              className="w-full h-full rounded-lg"
              src={`https://www.youtube.com/embed/${getYouTubeVideoId(
                article.youtubeUrl
              )}?autoplay=0&controls=1`}
              title={article.title}
              frameBorder="0"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        )}

        {article.shortVideo && (
          <div className="my-8 mx-auto max-w-sm">
            <div className="aspect-[9/16]">
              <iframe
                className="w-full h-full rounded-lg"
                src={`https://www.youtube.com/embed/${getYouTubeVideoId(
                  article.shortVideo
                )}?autoplay=0`}
                title={article.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        )}
      </div>

      {relatedArticles.length > 0 && (
        <div className="mt-12 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-4 border-b-2 border-red-600 pb-2">
            {textData.headings.relatedNews}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedArticles.map((news) => (
              <div
                key={news.id}
                className="bg-white rounded-lg shadow-md overflow-hidden transform hover:-translate-y-1 transition-transform duration-300"
              >
                <div className="relative">
                  <div
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" || e.key === " ")
                        navigate(`/news/${news.slug}`);
                    }}
                    onClick={() => navigate(`/news/${news.slug}`)}
                    className="cursor-pointer"
                  >
                    <img
                      src={getImageUrl(news, "News", imageVersion)}
                      alt={news.title}
                      className="w-full h-32 object-cover"
                    />
                    <div className="p-3">
                      <div className="mb-2 flex flex-wrap gap-2">
                        {(parseCategories(news.categories) || []).map(
                          (c, idx) => (
                            <span
                              key={idx}
                              className="text-xs font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full capitalize"
                            >
                              {c}
                            </span>
                          )
                        )}
                      </div>
                      <h4 className="font-bold text-md mb-1 h-16 overflow-hidden">
                        {news.title}
                      </h4>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500">
                          {formatDate(news.createdAt)}
                        </p>
                        <InlineShareButtons
                          url={`${window.location.origin}/news/${news.slug}`}
                          title={news.title}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </main>
  );
};

export default SingleNewsPage;
